import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Plus, Image, Video, Type, Trash2, Edit, 
  GripVertical, Eye, Monitor, Save, ExternalLink,
  Upload, Loader2, FileImage, FileVideo, Link2,
  ChevronUp, ChevronDown, AlertCircle, HardDrive
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from '@/components/ui/badge';
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Alert,
  AlertDescription,
} from "@/components/ui/alert";

const TIPOS_CONTENIDO = [
  { value: 'IMAGEN', label: 'Imagen', icon: Image },
  { value: 'VIDEO', label: 'Video', icon: Video },
  { value: 'TEXTO', label: 'Texto', icon: Type }
];

const DisplayAdminPage = () => {
  const [contenido, setContenido] = useState([]);
  const [archivos, setArchivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [limits, setLimits] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    tipo: 'IMAGEN',
    titulo: '',
    contenido: '',
    duracion: 10,
    sourceType: 'upload' // 'upload' or 'url'
  });

  const fetchContenido = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/display-content?active_only=false`, {
        withCredentials: true
      });
      setContenido(response.data);
    } catch (error) {
      console.error('Error fetching contenido:', error);
      toast.error('Error al cargar el contenido');
    }
  }, []);

  const fetchArchivos = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/display-files`, {
        withCredentials: true
      });
      setArchivos(response.data);
    } catch (error) {
      console.error('Error fetching archivos:', error);
    }
  }, []);

  const fetchLimits = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/upload/limits`, {
        withCredentials: true
      });
      setLimits(response.data);
    } catch (error) {
      console.error('Error fetching limits:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchContenido(), fetchArchivos(), fetchLimits()]);
      setLoading(false);
    };
    loadData();
  }, [fetchContenido, fetchArchivos, fetchLimits]);

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      const isInternalUrl = item.contenido?.startsWith('/api/files/');
      setFormData({
        tipo: item.tipo,
        titulo: item.titulo || '',
        contenido: item.contenido,
        duracion: item.duracion,
        sourceType: isInternalUrl ? 'upload' : 'url'
      });
    } else {
      setEditingItem(null);
      setFormData({
        tipo: 'IMAGEN',
        titulo: '',
        contenido: '',
        duracion: 10,
        sourceType: 'upload'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setFormData({
      tipo: 'IMAGEN',
      titulo: '',
      contenido: '',
      duracion: 10,
      sourceType: 'upload'
    });
    setUploadProgress(0);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Determine file type
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    if (!isImage && !isVideo) {
      toast.error('Solo se permiten imágenes y videos');
      return;
    }

    // Check size limits
    if (limits) {
      const maxSize = isImage ? limits.image.max_size_bytes : limits.video.max_size_bytes;
      if (file.size > maxSize) {
        const maxMB = isImage ? limits.image.max_size_mb : limits.video.max_size_mb;
        toast.error(`Archivo muy grande. Máximo: ${maxMB} MB`);
        return;
      }
    }

    // Upload file
    setUploading(true);
    setUploadProgress(0);

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/display-media`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      // Set the URL in the form
      setFormData(prev => ({
        ...prev,
        contenido: response.data.url,
        tipo: response.data.tipo,
        titulo: prev.titulo || file.name.replace(/\.[^/.]+$/, "")
      }));

      toast.success(`${response.data.tipo === 'IMAGEN' ? 'Imagen' : 'Video'} subido correctamente (${response.data.size_mb} MB)`);
      fetchArchivos();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir archivo');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSelectExistingFile = (file) => {
    setFormData(prev => ({
      ...prev,
      contenido: file.url,
      tipo: file.tipo,
      titulo: prev.titulo || file.original_filename?.replace(/\.[^/.]+$/, "") || ''
    }));
    toast.success('Archivo seleccionado');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.contenido.trim()) {
      toast.error('El contenido es requerido');
      return;
    }

    try {
      const submitData = {
        tipo: formData.tipo,
        titulo: formData.titulo,
        contenido: formData.contenido,
        duracion: formData.duracion
      };

      if (editingItem) {
        await axios.put(`${API}/display-content/${editingItem.id}`, submitData, {
          withCredentials: true
        });
        toast.success('Contenido actualizado');
      } else {
        await axios.post(`${API}/display-content`, {
          ...submitData,
          orden: contenido.length
        }, { withCredentials: true });
        toast.success('Contenido agregado');
      }
      handleCloseDialog();
      fetchContenido();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este contenido?')) return;
    
    try {
      await axios.delete(`${API}/display-content/${id}`, { withCredentials: true });
      toast.success('Contenido eliminado');
      fetchContenido();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('¿Eliminar este archivo? Los contenidos que lo usen dejarán de funcionar.')) return;
    
    try {
      await axios.delete(`${API}/display-files/${fileId}`, { withCredentials: true });
      toast.success('Archivo eliminado');
      fetchArchivos();
    } catch (error) {
      toast.error('Error al eliminar archivo');
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await axios.put(`${API}/display-content/${item.id}`, {
        active: !item.active
      }, { withCredentials: true });
      toast.success(item.active ? 'Contenido desactivado' : 'Contenido activado');
      fetchContenido();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const moveItem = async (index, direction) => {
    const newItems = [...contenido];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newItems.length) return;
    
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    
    const reorderedItems = newItems.map((item, idx) => ({
      id: item.id,
      orden: idx
    }));
    
    try {
      await axios.post(`${API}/display-content/reorder`, reorderedItems, {
        withCredentials: true
      });
      fetchContenido();
    } catch (error) {
      toast.error('Error al reordenar');
    }
  };

  const getTipoIcon = (tipo) => {
    const found = TIPOS_CONTENIDO.find(t => t.value === tipo);
    return found ? <found.icon className="w-4 h-4" /> : null;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getPreviewUrl = (contenido) => {
    if (!contenido) return null;
    if (contenido.startsWith('/api/')) {
      return `${API.replace('/api', '')}${contenido}`;
    }
    return contenido;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="display-admin-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Monitor className="w-7 h-7 text-blue-600" />
              Gestión de Pantalla
            </h1>
            <p className="text-slate-500">Administra el contenido de la pantalla de sala de espera</p>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <a href="/display" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                Ver Pantalla
              </a>
            </Button>
            <Button onClick={() => handleOpenDialog()} data-testid="add-content-btn">
              <Plus className="w-4 h-4 mr-2" />
              Agregar Contenido
            </Button>
          </div>
        </div>

        {/* Info Alert */}
        {limits && (
          <Alert className="bg-blue-50 border-blue-200">
            <HardDrive className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-700">
              <strong>Límites de archivos:</strong> Imágenes hasta {limits.image.max_size_mb} MB (JPG, PNG, WebP, GIF) | 
              Videos hasta {limits.video.max_size_mb} MB (MP4, WebM) | Para mejor rendimiento, usa videos de máximo 60 segundos.
            </AlertDescription>
          </Alert>
        )}

        {/* Tabs */}
        <Tabs defaultValue="contenido" className="space-y-4">
          <TabsList>
            <TabsTrigger value="contenido" className="gap-2">
              <Monitor className="w-4 h-4" />
              Contenido ({contenido.length})
            </TabsTrigger>
            <TabsTrigger value="archivos" className="gap-2">
              <HardDrive className="w-4 h-4" />
              Archivos ({archivos.length})
            </TabsTrigger>
          </TabsList>

          {/* Contenido Tab */}
          <TabsContent value="contenido">
            <Card>
              <CardHeader>
                <CardTitle>Contenido de la Pantalla</CardTitle>
                <CardDescription>
                  Arrastra para reordenar. El contenido activo se mostrará en la pantalla.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {contenido.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Monitor className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">No hay contenido configurado</p>
                    <p className="text-sm">Agrega imágenes, videos o texto para mostrar en la pantalla</p>
                    <Button onClick={() => handleOpenDialog()} className="mt-4">
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar Contenido
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {contenido.map((item, index) => (
                      <div
                        key={item.id}
                        className={`flex items-center gap-4 p-4 rounded-lg border ${
                          item.active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'
                        }`}
                      >
                        <div className="flex flex-col gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            onClick={() => moveItem(index, 'up')}
                            disabled={index === 0}
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            onClick={() => moveItem(index, 'down')}
                            disabled={index === contenido.length - 1}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Preview */}
                        <div className="w-24 h-16 bg-slate-100 rounded overflow-hidden flex-shrink-0">
                          {item.tipo === 'IMAGEN' ? (
                            <img
                              src={getPreviewUrl(item.contenido)}
                              alt={item.titulo}
                              className="w-full h-full object-cover"
                              onError={(e) => e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><rect width="24" height="24"/><text x="12" y="16" text-anchor="middle" font-size="8">Error</text></svg>'}
                            />
                          ) : item.tipo === 'VIDEO' ? (
                            <div className="w-full h-full flex items-center justify-center bg-slate-200">
                              <Video className="w-8 h-8 text-slate-400" />
                            </div>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-slate-200">
                              <Type className="w-8 h-8 text-slate-400" />
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="gap-1">
                              {getTipoIcon(item.tipo)}
                              {item.tipo}
                            </Badge>
                            <span className="text-sm text-slate-500">{item.duracion}s</span>
                          </div>
                          <p className="font-medium text-slate-900 truncate">
                            {item.titulo || 'Sin título'}
                          </p>
                          <p className="text-xs text-slate-400 truncate">
                            {item.contenido?.startsWith('/api/') ? '📁 Archivo local' : item.contenido}
                          </p>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={item.active}
                            onCheckedChange={() => handleToggleActive(item)}
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleOpenDialog(item)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDelete(item.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Archivos Tab */}
          <TabsContent value="archivos">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Archivos Subidos</CardTitle>
                    <CardDescription>
                      Archivos almacenados en el sistema
                    </CardDescription>
                  </div>
                  <Button onClick={() => fileInputRef.current?.click()}>
                    <Upload className="w-4 h-4 mr-2" />
                    Subir Archivo
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*,video/*"
                    onChange={handleFileSelect}
                  />
                </div>
              </CardHeader>
              <CardContent>
                {archivos.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <HardDrive className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">No hay archivos subidos</p>
                    <p className="text-sm">Sube imágenes o videos para usarlos en la pantalla</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {archivos.map((file) => (
                      <div
                        key={file.id}
                        className="border rounded-lg overflow-hidden bg-white group"
                      >
                        <div className="aspect-video bg-slate-100 relative">
                          {file.tipo === 'IMAGEN' ? (
                            <img
                              src={`${API.replace('/api', '')}${file.url}`}
                              alt={file.original_filename}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <FileVideo className="w-12 h-12 text-slate-300" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleSelectExistingFile(file)}
                            >
                              Usar
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteFile(file.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="p-2">
                          <p className="text-sm font-medium truncate" title={file.original_filename}>
                            {file.original_filename}
                          </p>
                          <div className="flex items-center justify-between text-xs text-slate-500">
                            <Badge variant="outline" className="text-xs">
                              {file.tipo}
                            </Badge>
                            <span>{formatFileSize(file.size)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? 'Editar Contenido' : 'Agregar Contenido'}
              </DialogTitle>
              <DialogDescription>
                Configura el contenido que se mostrará en la pantalla de sala de espera
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Tipo */}
              <div className="space-y-2">
                <Label>Tipo de contenido</Label>
                <Select
                  value={formData.tipo}
                  onValueChange={(v) => setFormData({ ...formData, tipo: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_CONTENIDO.map((tipo) => (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        <div className="flex items-center gap-2">
                          <tipo.icon className="w-4 h-4" />
                          {tipo.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Titulo */}
              <div className="space-y-2">
                <Label>Título (opcional)</Label>
                <Input
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  placeholder="Nombre del contenido"
                />
              </div>

              {/* Contenido - Tabs for Upload vs URL */}
              {formData.tipo !== 'TEXTO' && (
                <div className="space-y-2">
                  <Label>Origen del contenido</Label>
                  <Tabs value={formData.sourceType} onValueChange={(v) => setFormData({ ...formData, sourceType: v })}>
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="upload" className="gap-2">
                        <Upload className="w-4 h-4" />
                        Subir Archivo
                      </TabsTrigger>
                      <TabsTrigger value="url" className="gap-2">
                        <Link2 className="w-4 h-4" />
                        URL Externa
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="upload" className="space-y-3 pt-3">
                      {formData.contenido && formData.contenido.startsWith('/api/') ? (
                        <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                          {formData.tipo === 'IMAGEN' ? (
                            <img
                              src={getPreviewUrl(formData.contenido)}
                              alt="Preview"
                              className="w-20 h-14 object-cover rounded"
                            />
                          ) : (
                            <div className="w-20 h-14 bg-green-100 rounded flex items-center justify-center">
                              <FileVideo className="w-8 h-8 text-green-600" />
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-medium text-green-800">Archivo seleccionado</p>
                            <p className="text-xs text-green-600">{formData.contenido}</p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setFormData({ ...formData, contenido: '' })}
                          >
                            Cambiar
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div 
                            className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-slate-50 transition-colors"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            {uploading ? (
                              <div className="space-y-3">
                                <Loader2 className="w-10 h-10 mx-auto animate-spin text-blue-500" />
                                <Progress value={uploadProgress} className="w-48 mx-auto" />
                                <p className="text-sm text-slate-500">Subiendo... {uploadProgress}%</p>
                              </div>
                            ) : (
                              <>
                                <Upload className="w-10 h-10 mx-auto text-slate-400 mb-3" />
                                <p className="font-medium text-slate-700">Clic para seleccionar archivo</p>
                                <p className="text-sm text-slate-500">
                                  {formData.tipo === 'IMAGEN' ? 'JPG, PNG, WebP, GIF (máx 5MB)' : 'MP4, WebM (máx 50MB)'}
                                </p>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept={formData.tipo === 'IMAGEN' ? 'image/*' : 'video/*'}
                            onChange={handleFileSelect}
                          />

                          {/* Existing files */}
                          {archivos.filter(f => f.tipo === formData.tipo).length > 0 && (
                            <div className="space-y-2">
                              <Label className="text-sm">O selecciona un archivo existente:</Label>
                              <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto">
                                {archivos.filter(f => f.tipo === formData.tipo).map((file) => (
                                  <div
                                    key={file.id}
                                    className="aspect-video bg-slate-100 rounded overflow-hidden cursor-pointer hover:ring-2 ring-blue-500 transition-all"
                                    onClick={() => handleSelectExistingFile(file)}
                                  >
                                    {file.tipo === 'IMAGEN' ? (
                                      <img
                                        src={`${API.replace('/api', '')}${file.url}`}
                                        alt={file.original_filename}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <FileVideo className="w-6 h-6 text-slate-400" />
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </TabsContent>

                    <TabsContent value="url" className="pt-3">
                      <Input
                        value={formData.contenido}
                        onChange={(e) => setFormData({ ...formData, contenido: e.target.value })}
                        placeholder={formData.tipo === 'IMAGEN' ? 'https://ejemplo.com/imagen.jpg' : 'https://ejemplo.com/video.mp4'}
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Ingresa la URL directa de la {formData.tipo === 'IMAGEN' ? 'imagen' : 'video'}
                      </p>
                    </TabsContent>
                  </Tabs>
                </div>
              )}

              {/* Texto content */}
              {formData.tipo === 'TEXTO' && (
                <div className="space-y-2">
                  <Label>Contenido del texto</Label>
                  <Textarea
                    value={formData.contenido}
                    onChange={(e) => setFormData({ ...formData, contenido: e.target.value })}
                    placeholder="Escribe el mensaje que se mostrará..."
                    rows={4}
                  />
                </div>
              )}

              {/* Duracion */}
              <div className="space-y-2">
                <Label>Duración (segundos)</Label>
                <Input
                  type="number"
                  min={5}
                  max={300}
                  value={formData.duracion}
                  onChange={(e) => setFormData({ ...formData, duracion: parseInt(e.target.value) || 10 })}
                />
                <p className="text-xs text-slate-500">
                  Tiempo que se mostrará este contenido antes de pasar al siguiente (5-300 segundos)
                </p>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={uploading}>
                  <Save className="w-4 h-4 mr-2" />
                  {editingItem ? 'Guardar Cambios' : 'Agregar'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DisplayAdminPage;
